import React, { useCallback, useMemo } from 'react';
import _ from 'lodash';
import { SkillsMatrix, Props as SkillsMatrixProps } from './SkillsMatrix';
import { SkillSingleSelect } from '../common/SkillSingleSelect';
import Skeleton from 'react-loading-skeleton';
import ServerError from '../../../../../components/ServerError';
import styled from '@emotion/styled';
import { useSkillsMatrix } from './useSkillsMatrix';
import { SelectSingleEmployeeConnected } from '../../../components/Selects/SelectSingleEmployeeConnected';
import Button from '../../../../../components/button';
import { InfoHint } from '../../../../../components/hint/InfoHint';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import { useObjectLabels } from '../../../hooks/useObjectLabels';
import { useFlairLocalStorage } from '../../../hooks/useFlairLocalStorage';
import { SkillCategoryFilter } from '../manager/SkillCategoryFilter';

type Props = Pick<SkillsMatrixProps, 'onEmployeeClick'>;

export const SkillsMatrixConnected: React.FC<Props> = (props) => {
  const t = useNamespacedTranslation('skills.matrix');
  const objectLabels = useObjectLabels();
  const [categoryFilter, setCategoryFilter] = useFlairLocalStorage(
    'skillsMatrix_categoryId',
    null,
  );
  const {
    selectedEmployeeIds,
    setSelectedEmployeeIds,
    selectedSkillIds,
    setSelectedSkillIds,
    notSelectedSkills,
    selectedEmployeeInfos,
    selectedSkills,
    employeeSkills,
    error,
    resetSelection,
  } = useSkillsMatrix();

  const filteredSelectedSkills = useMemo(
    () =>
      categoryFilter
        ? selectedSkills?.filter((s) => s.category?.id === categoryFilter)
        : selectedSkills,
    [selectedSkills, categoryFilter],
  );
  const filteredNotSelectedSkills = useMemo(
    () =>
      categoryFilter
        ? notSelectedSkills?.filter((s) => s.category?.id === categoryFilter)
        : notSelectedSkills,
    [notSelectedSkills, categoryFilter],
  );

  const handleAddEmployee = useCallback(
    (employeeId: string | null) => {
      if (selectedEmployeeIds === null) {
        return;
      }
      if (employeeId) {
        setSelectedEmployeeIds(_.uniq([...selectedEmployeeIds, employeeId]));
      }
    },
    [selectedEmployeeIds, setSelectedEmployeeIds],
  );

  const handleResetClick = useCallback(() => {
    resetSelection();
    setCategoryFilter(null);
  }, [resetSelection, setCategoryFilter]);

  const handleRemoveEmployee = useCallback(
    (employeeId) => {
      if (selectedEmployeeIds === null) {
        return;
      }
      setSelectedEmployeeIds(
        selectedEmployeeIds.filter((x) => x !== employeeId),
      );
    },
    [setSelectedEmployeeIds, selectedEmployeeIds],
  );

  const handleAddSkill = useCallback(
    (skillId) => {
      if (selectedSkillIds === null) {
        return;
      }
      setSelectedSkillIds(_.uniq([...selectedSkillIds, skillId]));
    },
    [setSelectedSkillIds, selectedSkillIds],
  );

  const handleRemoveSkill = useCallback(
    (skillId) => {
      if (selectedSkillIds === null) {
        return;
      }
      setSelectedSkillIds(selectedSkillIds.filter((x) => x !== skillId));
    },
    [setSelectedSkillIds, selectedSkillIds],
  );

  if (error) {
    return <ServerError />;
  }

  const renderSkillSelect = () => {
    return filteredNotSelectedSkills ? (
      <SkillSingleSelect
        value={null}
        placeholder={t('addSkillPlaceholder')}
        allSkills={filteredNotSelectedSkills}
        onChange={handleAddSkill}
        className="small"
      />
    ) : (
      <Skeleton width={100} height={40} />
    );
  };

  const renderEmployeeSelect = () => {
    return (
      <SelectSingleEmployeeConnected
        value={null}
        heightMode="normal"
        employeeOptionLabelClassName="small"
        className="small"
        placeholder={objectLabels.employeePlural}
        excludeIds={selectedEmployeeIds ?? []}
        onChange={handleAddEmployee}
      />
    );
  };

  if (
    !selectedEmployeeIds ||
    !selectedSkillIds ||
    !selectedSkills ||
    !selectedEmployeeInfos
  ) {
    return <Loading />;
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center px-4">
        <div>
          <Button
            label={t('resetMatrix')}
            onClick={handleResetClick}
            size="sm"
            variant="outline-secondary"></Button>
          <InfoHint text={t('resetMatrixHint')} className="ms-2" />
        </div>
        <div className="d-flex align-items-center gap-2">
          <CategoryFilterContainer>
            <SkillCategoryFilter
              value={categoryFilter}
              onChange={setCategoryFilter}
              className="small"
            />
          </CategoryFilterContainer>
          <SkillSelectContainer>{renderSkillSelect()}</SkillSelectContainer>
        </div>
      </div>
      {categoryFilter &&
        selectedSkills.length > 0 &&
        (filteredSelectedSkills?.length ?? 0) === 0 && (
          <div className="px-4 pt-3">
            <div className="alert alert-info d-flex justify-content-between align-items-center mb-0">
              <span>{t('filterHidesAllSelectedSkills')}</span>
              <Button
                label={t('clearCategoryFilter')}
                size="sm"
                variant="link"
                onClick={() => setCategoryFilter(null)}
              />
            </div>
          </div>
        )}
      <SkillsMatrixContainer className="py-4">
        <SkillsMatrix
          {...props}
          employees={selectedEmployeeInfos}
          skills={filteredSelectedSkills ?? []}
          employeeSkills={employeeSkills ?? 'loading'}
          onRemoveEmployeeClick={handleRemoveEmployee}
          onRemoveSkillClick={handleRemoveSkill}
          topLeftCell={renderEmployeeSelect()}
        />
      </SkillsMatrixContainer>
    </div>
  );
};

const Loading = () => {
  return (
    <div className="px-4 pb-4">
      <div className="d-flex justify-content-between align-items-center">
        <Skeleton width="5rem" height="1.5rem" />
        <Skeleton width="7.5rem" height="1.5rem" />
      </div>
      <Skeleton className="mt-4" width="100%" height={400} />
    </div>
  );
};

const SkillsMatrixContainer = styled.div({
  maxWidth: '100%',
  overflowY: 'auto',
});

const SkillSelectContainer = styled.div({
  minWidth: '12rem',
});

const CategoryFilterContainer = styled.div({
  minWidth: '12rem',
});
